// Reads back which Consent Mode defaults Google actually applied for this
// visitor, without the visitor's country ever reaching page JS.
//
// Google resolves the `region:` keys of a `consent default` command inside
// gtm.js, so the outcome is not knowable from what we pushed — `dataLayer` is
// a push queue of what *we* put on it, and gtm.js never pushes the resolved
// state back onto it. The outcome therefore has to be observed from one of two
// client-visible surfaces, in this order:
//
//   1. A `consent_defaults_resolved` custom event published by a GTM-console
//      Custom HTML tag. That tag is manual work outside this repo; until it is
//      published, surface 2 is the only live path. It must push exactly:
//
//        window.dataLayer.push({
//          event: 'consent_defaults_resolved',
//          analytics_storage: <'granted' | 'denied'>,
//          ad_storage: <'granted' | 'denied'>,
//          ad_user_data: <'granted' | 'denied'>,
//          ad_personalization: <'granted' | 'denied'>,
//        });
//
//      A plain `{event: ...}` object — a Custom Event for GTM's trigger
//      system, not a `gtagCommand()` `arguments` object.
//
//   2. `window.google_tag_data.ics`, gtm.js's own resolved-consent object,
//      whose per-type entries carry the `default` Google applied. This is an
//      undocumented, unversioned gtm.js internal with no shape guarantee, so
//      it is read defensively in both observed forms and verified only in GTM
//      preview.
//
// Both surfaces only exist after gtm.js executes, which is why observation is
// a bounded poll rather than a synchronous read. Anything this module cannot
// prove is `null` — never a partial and never a guess — so the consent
// screen's safe direction is always all-denied.

// Declared here rather than beside `CONSENT_UPDATED_EVENT` in
// `gtm-consent-mode.ts`: that module is the site's consent *writer*, and this
// event is one the site only ever consumes.
export const CONSENT_DEFAULTS_RESOLVED_EVENT = 'consent_defaults_resolved';

// The two visitor-facing categories the consent screen can seed. Deliberately
// not `ConsentCategories` — there is no `necessary: true` here, because a
// resolved default is an observation about Google's state, not a visitor
// decision.
export type ResolvedRegionalDefaults = {
  analytics: boolean;
  advertising: boolean;
};

// Surface 1: the payload the GTM-console Custom HTML tag publishes. Every
// signal is a plain string, never a boolean. Documented as a contract this
// repo cannot enforce from source; the parsers take `unknown` and narrow.
export type ConsentDefaultsResolvedEvent = {
  event: typeof CONSENT_DEFAULTS_RESOLVED_EVENT;
  analytics_storage: string;
  ad_storage: string;
  ad_user_data?: string;
  ad_personalization?: string;
};

// Surface 2: one entry per Consent Mode type. `default` is the value Google
// applied for this visitor's resolved region; `update` is our own replayed
// cookie coming back at us and is never read.
export type GoogleTagIcsEntry = {
  default?: string;
  update?: string;
};

// `window.google_tag_data.ics`, observed in two shapes: entries keyed under
// `entries`, and (older containers) keyed directly on `ics`.
export type GoogleTagIcs = {
  entries?: Record<string, GoogleTagIcsEntry | undefined>;
  [consentType: string]: unknown;
};

export const RESOLVED_DEFAULTS_POLL_INTERVAL_MS = 100;
export const RESOLVED_DEFAULTS_DEADLINE_MS = 3000;

const GRANTED = 'granted';
const ANALYTICS_SIGNAL = 'analytics_storage';
const REQUIRED_AD_SIGNAL = 'ad_storage';
const AD_SIGNALS = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
] as const;

type ResolvedDefaultsWindow = Window & {
  dataLayer?: unknown[];
  google_tag_data?: { ics?: unknown };
};

// The shape gate and the value mapping, in that order. A surface is usable
// only when it carries `analytics_storage` and `ad_storage` as strings;
// `ad_user_data` / `ad_personalization` never invalidate it, but they do
// participate in the verdict when present.
//
// `advertising` is the conjunction of every `ad_*` signal the surface
// carries — the ALL rule. Collapsing three signals into one boolean by ANY
// would report "Advertising is on" to a visitor for whom two of the three are
// denied, overstating what the site does; ALL can only ever under-report, and
// a toggle the visitor can switch back on is the recoverable direction. The
// mixed verdict (analytics granted, advertising denied) is a real outcome,
// not an edge case: it is what the UK and any GPC visitor resolve to.
// The two surfaces spell a granted default differently, and both spellings are
// live. The `consent_defaults_resolved` event carries what the console tag
// pushes, which is the gtag vocabulary: the literal string 'granted'.
// `google_tag_data.ics` is gtm.js's own internal bookkeeping and records a
// BOOLEAN — an observed entry reads
// `{region: 'US-CA', implicit: true, default: true, quiet: false}`. Reading
// only strings made the ics path return null on every visit in production,
// which showed every visitor an all-denied screen while GTM had granted them
// analytics. Anything that is neither the exact literal nor `true` is denied,
// so the widening cannot turn an unknown value into a grant.
function isGranted(value: unknown): boolean {
  return value === GRANTED || value === true;
}

// A signal counts as readable when it is present as one of the two known
// spellings. This is the shape gate: it is what separates "we do not know"
// (null, keep polling, stay denied) from "resolved to denied" (a real answer),
// so it must not accept a shape whose meaning we cannot state.
function isReadableSignal(value: unknown): boolean {
  return typeof value === 'string' || typeof value === 'boolean';
}

function deriveDefaults(
  readSignal: (consentType: string) => unknown,
): ResolvedRegionalDefaults | null {
  const analytics = readSignal(ANALYTICS_SIGNAL);
  const adStorage = readSignal(REQUIRED_AD_SIGNAL);
  if (!isReadableSignal(analytics) || !isReadableSignal(adStorage)) {
    return null;
  }
  let advertising = true;
  for (const consentType of AD_SIGNALS) {
    const value = readSignal(consentType);
    if (!isReadableSignal(value)) continue;
    if (!isGranted(value)) advertising = false;
  }
  return { analytics: isGranted(analytics), advertising };
}

function readEntryMapSignal(map: Record<string, unknown>, consentType: string) {
  const entry = map[consentType];
  if (!entry || typeof entry !== 'object') return undefined;
  return (entry as Record<string, unknown>).default;
}

// The raw `dataLayer` array, or anything at all. The last entry whose `event`
// matches wins — if the console tag ever pushes more than once in a page's
// life (a corrective push, a second container, a re-fire on a trigger), the
// end of the queue is the current state and an earlier match is superseded.
// This rule is scoped to a single read: `subscribeToResolvedDefaults` settles
// on its first success, so a corrective event pushed on a later tick is never
// observed.
export function parseResolvedDefaultsFromDataLayer(
  dataLayer: unknown,
): ResolvedRegionalDefaults | null {
  if (!Array.isArray(dataLayer)) return null;
  let latest: Record<string, unknown> | null = null;
  for (const entry of dataLayer) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    if (record.event === CONSENT_DEFAULTS_RESOLVED_EVENT) latest = record;
  }
  if (!latest) return null;
  const signals = latest;
  return deriveDefaults((consentType) => signals[consentType]);
}

// `window.google_tag_data.ics`, or anything at all. `entries` is read first,
// and a miss there FALLS THROUGH to the flat keys rather than returning null:
// a real container may carry a present-but-incomplete `entries` object
// alongside usable flat keys. The fall-through discards a partial `entries`
// read wholesale rather than merging one type from each shape — a merged read
// would be exactly the partial result this module must never produce.
export function parseResolvedDefaultsFromIcs(
  ics: unknown,
): ResolvedRegionalDefaults | null {
  if (!ics || typeof ics !== 'object') return null;
  const record = ics as Record<string, unknown>;
  const entries = record.entries;
  if (entries && typeof entries === 'object') {
    const entryMap = entries as Record<string, unknown>;
    const fromEntries = deriveDefaults((consentType) =>
      readEntryMapSignal(entryMap, consentType),
    );
    if (fromEntries) return fromEntries;
  }
  return deriveDefaults((consentType) =>
    readEntryMapSignal(record, consentType),
  );
}

// The dataLayer event first, the ics fallback second. Returns null when
// `window` is undefined (SSR / static export) or when neither surface yields a
// complete result.
export function readResolvedRegionalDefaults(): ResolvedRegionalDefaults | null {
  if (typeof window === 'undefined') return null;
  const scope = window as ResolvedDefaultsWindow;
  const fromEvent = parseResolvedDefaultsFromDataLayer(scope.dataLayer);
  if (fromEvent) return fromEvent;
  return parseResolvedDefaultsFromIcs(scope.google_tag_data?.ics);
}

// Shortens the observation window for a test that needs the deadline to
// elapse in real time. Production callers pass nothing and get the exported
// constants, so the shipped cadence lives in exactly one place.
export type ResolvedDefaultsPollOptions = {
  intervalMs?: number;
  deadlineMs?: number;
};

// Bounded observation. One synchronous read first — if it succeeds, no timer
// is ever created. Otherwise poll until the first successful read or until the
// deadline elapses, whichever comes first. `onResolved` fires at most once and
// never with null: on the deadline the subscriber simply stops and the caller
// keeps its all-denied state. Elapsed time is measured against the clock
// rather than counted in ticks, so a background-throttled interval cannot
// overrun the deadline. The returned cleanup is idempotent and safe to call
// after the deadline — no timer may outlive the banner.
export function subscribeToResolvedDefaults(
  onResolved: (defaults: ResolvedRegionalDefaults) => void,
  options?: ResolvedDefaultsPollOptions,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const immediate = readResolvedRegionalDefaults();
  if (immediate) {
    onResolved(immediate);
    return () => {};
  }

  const intervalMs = options?.intervalMs ?? RESOLVED_DEFAULTS_POLL_INTERVAL_MS;
  const deadlineMs = options?.deadlineMs ?? RESOLVED_DEFAULTS_DEADLINE_MS;
  const startedAt = Date.now();
  let timer: ReturnType<typeof setInterval> | undefined;

  function stop(): void {
    if (timer === undefined) return;
    clearInterval(timer);
    timer = undefined;
  }

  timer = setInterval(() => {
    // A read can throw: both surfaces are plain `window` properties that an
    // extension, a test harness or a hostile page can replace with a throwing
    // accessor. An escaping exception would skip the deadline check below and
    // leave this interval alive for the life of the page, so a throwing
    // surface counts as "not resolved yet" and still stops at the deadline.
    let resolved: ResolvedRegionalDefaults | null = null;
    try {
      resolved = readResolvedRegionalDefaults();
    } catch {
      resolved = null;
    }
    if (resolved) {
      stop();
      onResolved(resolved);
      return;
    }
    if (Date.now() - startedAt >= deadlineMs) stop();
  }, intervalMs);

  return stop;
}
